import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { useAuth } from "@/hooks/useAuth";
import { useDoctors } from "@/hooks/useDoctors";
import { showSuccess, showError } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";

import PrescriptionOverview from "../components/prescriptions/PrescriptionOverview";
import PrescriptionList from "../components/prescriptions/PrescriptionList";
import PrescriptionForm from "../components/prescriptions/PrescriptionForm";
import PrescriptionDetail from "../components/prescriptions/PrescriptionDetail";
import DispenseModal from "../components/prescriptions/DispenseModal";

export default function Prescriptions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showForm, setShowForm] = useState(false);
  const [showDispenseModal, setShowDispenseModal] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [prescriptionIdToDispense, setPrescriptionIdToDispense] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [dispenseStatusFilter, setDispenseStatusFilter] = useState("all");

  // Determine if user is a patient (affects UI permissions)
  const isPatient = user && user.userType === "patient";

  // Fetch prescriptions using React Query
  const {
    data: prescriptions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["prescriptions"],
    queryFn: () => apiClient.get(ENDPOINTS.PRESCRIPTIONS.LIST),
  });

  // Fetch doctors for name lookup using custom hook
  const { data: doctors = [] } = useDoctors();

  // Fetch single prescription for dispensing modal
  const { data: prescriptionToDispense } = useQuery({
    queryKey: ["prescription", prescriptionIdToDispense],
    queryFn: () =>
      apiClient.get(ENDPOINTS.PRESCRIPTIONS.GET(prescriptionIdToDispense)),
    enabled: !!prescriptionIdToDispense, // Only fetch when ID is set
  });

  // Handle prescription ID from query parameter (e.g., from transaction history)
  useEffect(() => {
    const prescriptionId = searchParams.get("prescriptionId");
    if (prescriptionId && prescriptions.length > 0 && !selectedPrescription) {
      const prescription = prescriptions.find((p) => p.id === prescriptionId);
      if (prescription) {
        setSelectedPrescription(prescription);
        // Clear the query parameter after selecting
        setSearchParams({});
      }
    }
  }, [prescriptions, searchParams, selectedPrescription, setSearchParams]);


  // Create prescription mutation
  const createPrescription = useMutation({
    mutationFn: (data) => apiClient.post(ENDPOINTS.PRESCRIPTIONS.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["prescriptions"]);
      setShowForm(false);
      setEditingPrescription(null);
      showSuccess("Prescription created successfully");
    },
    onError: (error) => {
      console.error("Failed to create prescription:", error);
      showError(
        "Failed to create prescription",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Update prescription mutation
  const updatePrescription = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.put(ENDPOINTS.PRESCRIPTIONS.UPDATE(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries(["prescriptions"]);
      setShowForm(false);
      setEditingPrescription(null);
      showSuccess("Prescription updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update prescription:", error);
      showError(
        "Failed to update prescription",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Delete prescription mutation
  const deletePrescription = useMutation({
    mutationFn: (id) => apiClient.delete(ENDPOINTS.PRESCRIPTIONS.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries(["prescriptions"]);
      if (selectedPrescription?.id === arguments[0]) {
        setSelectedPrescription(null);
      }
      showSuccess("Prescription deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete prescription:", error);
      showError("Failed to delete prescription", getErrorMessage(error));
    },
  });

  const handleAddPrescription = () => {
    setEditingPrescription(null);
    setShowForm(true);
  };

  const handleEditPrescription = (prescription) => {
    setEditingPrescription(prescription);
    setSelectedPrescription(null); // Close detail view if open
    setShowForm(true);
  };

  const handleSavePrescription = async (data) => {
    try {
      if (editingPrescription) {
        await updatePrescription.mutateAsync({
          id: editingPrescription.id,
          data,
        });
      } else {
        await createPrescription.mutateAsync(data);
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleDeletePrescription = async (prescriptionId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this prescription? This action cannot be undone."
      )
    ) {
      try {
        await deletePrescription.mutateAsync(prescriptionId);
      } catch (error) {
        // Error handling is done in the mutation's onError callback
      }
    }
  };

  const handleDispensePrescription = (prescription) => {
    setPrescriptionIdToDispense(prescription.id);
    setShowDispenseModal(true);
  };

  const filteredPrescriptions = prescriptions.filter((prescription) => {
    const searchMatch =
      prescription.patientId
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      prescription.patientEmail
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
    const dispenseMatch =
      dispenseStatusFilter === "all" ||
      prescription.dispenseStatus === dispenseStatusFilter;
    return searchMatch && dispenseMatch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {isPatient ? "My Prescriptions" : "Prescriptions"}
          </h1>
          <p className="text-slate-600">
            {isPatient
              ? "View your prescriptions and medication history"
              : "Generate and manage prescriptions for patients."}
          </p>
        </div>
        {!isPatient && (
          <div className="flex gap-3">
            <Button
              onClick={handleAddPrescription}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Prescription
            </Button>
          </div>
        )}
      </div>

      {/* Overview Stats */}
      <PrescriptionOverview prescriptions={prescriptions} />

      {/* Detail View */}
      {selectedPrescription ? (
        <PrescriptionDetail
          prescription={selectedPrescription}
          doctors={doctors}
          onClose={() => setSelectedPrescription(null)}
          onEdit={!isPatient ? handleEditPrescription : undefined}
          onDelete={!isPatient ? handleDeletePrescription : undefined}
          onDispense={!isPatient ? handleDispensePrescription : undefined}
          isPatientView={isPatient}
        />
      ) : (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={
                  isPatient
                    ? "Search by medication name or doctor..."
                    : "Search by patient ID or doctor..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
              />
            </div>

            {!isPatient && (
              <select
                value={dispenseStatusFilter}
                onChange={(e) => setDispenseStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
              >
                <option value="all">All Dispense Status</option>
                <option value="pending">Pending</option>
                <option value="partially_dispensed">Partially Dispensed</option>
                <option value="fully_dispensed">Fully Dispensed</option>
                <option value="external">External Pharmacy</option>
              </select>
            )}
          </div>

          {/* Prescriptions List */}
          <PrescriptionList
            prescriptions={filteredPrescriptions}
            doctors={doctors}
            isLoading={isLoading}
            onSelectPrescription={setSelectedPrescription}
            onEditPrescription={!isPatient ? handleEditPrescription : undefined}
            onDeletePrescription={
              !isPatient ? handleDeletePrescription : undefined
            }
            onDispensePrescription={
              !isPatient ? handleDispensePrescription : undefined
            }
            isPatientView={isPatient}
          />
        </>
      )}

      {/* Form Modal - Only for non-patients */}
      {!isPatient && showForm && (
        <PrescriptionForm
          prescription={editingPrescription}
          onSave={handleSavePrescription}
          onClose={() => {
            setShowForm(false);
            setEditingPrescription(null);
          }}
        />
      )}

      {/* Dispense Modal - Only for non-patients */}
      {!isPatient && showDispenseModal && prescriptionToDispense && (
        <DispenseModal
          prescription={prescriptionToDispense}
          onClose={() => {
            setShowDispenseModal(false);
            setPrescriptionIdToDispense(null);
          }}
        />
      )}
    </div>
  );
}
